import React, { useState, useEffect, useContext } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, MenuItem, Box, Modal, Typography, Button
} from "@mui/material";
import axios from "axios";
import { UserContext } from "../UserContext";
import LabeledTextField from "./LabeledTextField";
import SummarizeIcon from '@mui/icons-material/Summarize';
import "./Attendance.css"


export const Attendance = () => {
  const currentMonth = new Date().getMonth() + 1; // Default to current month
  const currentYear = new Date().getFullYear(); // Default to current year

  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [modalOpen, setModalOpen] = useState(false);
  const [logDetails, setLogDetails] = useState(null);

  const itemsPerPage = 4;
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(attendanceData.length / itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = attendanceData.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [attendanceData]);

  const { user_id, role } = useContext(UserContext);

  const years = [currentYear, currentYear - 1, currentYear - 2];

  useEffect(() => {
    fetchAttendance(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear]);

  const fetchAttendance = async (month, year) => {
    try {
      const params = new URLSearchParams();
      if (role === "Employee" && user_id) {
        params.append("user_id", user_id);
      }
      params.append("month", month);
      params.append("year", year);

      const response = await axios.get(`api/login-logout-logs/?${params.toString()}`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    }
  };

  const fetchLogDetails = async (userId, date) => {
    try {
      const response = await axios.get(
        `api/login-logout-logs/details/`,
        { params: { user_id: userId, date } }
      );
      setLogDetails(response.data);
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching log details:", error);
    }
  };

  const [currentDate, setCurrentDate] = useState(getFormattedDate());

  function getFormattedDate() {
    const now = new Date();
    const day = now.toLocaleString("en-US", { weekday: "short" });
    const month = now.toLocaleString("en-US", { month: "short" });
    const date = now.toLocaleString("en-US", { day: "2-digit" });
    const year = now.getFullYear();
    const time = now.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${day}, ${month} ${date}, ${year} ${time}`;
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(getFormattedDate());
    }, 1000);
    return () => clearInterval(timer);
  }, []);


  return (
    <Box>
      <Box>
        <div className="attendance-container">
          <Box className="attendance-header">
            <SummarizeIcon className="attendance-icon" />
            <span className="title">Attendance</span>
          </Box>
          <span className="title_current-date">{currentDate}</span>
        </div>
        <Box className="selection-box">
          <LabeledTextField
            select
            label="Month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
            fullWidth
          >
            {[...Array(12)].map((_, index) => (
              <MenuItem key={index} value={index + 1}>
                {new Date(0, index).toLocaleString("en", { month: "long" })}
              </MenuItem>
            ))}
          </LabeledTextField>
          <LabeledTextField
            select
            label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            fullWidth
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </LabeledTextField>
        </Box>


        <Box>
          <TableContainer
            sx={{
              maxWidth: "1115px", // Increased max width for wider table
              width: "100%", // Full width within container
              margin: "0 auto", // Center-align the table container
              overflowX: "auto", // Enable horizontal scrolling
              backgroundColor: "#fff",
              borderRadius: "4px",
              boxShadow: "none",
              marginLeft:"33px",
              p: "40px 40px 40px 0px",
              "&::-webkit-scrollbar": {
                display: 'none'
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "4px",
              },  
            }}
          >
            <Table
              sx={{
                width: "max-content", // Allow table to take natural width
                minWidth: "1200px", // Increased min width for wider table
                borderCollapse: "separate",
                borderSpacing: "0 16px",
                backgroundColor: "#fff",
                borderRadius: "4px",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      backgroundColor: "#fb7102",
                      color: "#fff",
                      fontWeight: 800,
                      fontFamily: 'Josefin Sans',
                      width: "200px", // Increased width for user column
                      borderTopLeftRadius: "10px",
                      borderBottomLeftRadius: "10px",
                      textAlign: "left",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    User
                  </TableCell>
                  {Array.from({ length: 31 }, (_, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        backgroundColor: "#fb7102",
                        color: "#fff",
                        fontWeight: 800,
                        fontFamily: 'Josefin Sans',
                        textAlign: "center",
                        width: "60px", // Increased width for day columns
                        borderRight: index === 30 ? "1px solid #fff" : "none",
                        borderTopRightRadius: index === 30 ? "10px" : "0",
                        borderBottomRightRadius: index === 30 ? "10px" : "0",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {index + 1}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.map((userData, rowIndex) => (
                  <TableRow
                    key={userData.user_id}
                    sx={{
                      transition: "transform 0.4s, box-shadow 0.4s, background-color 0.4s",
                      "&:hover": {
                        transform: "scaleY(1.05)",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                        backgroundColor: "#f5f5f5",
                        fontFamily: 'Josefin Sans',
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        backgroundColor: "#fff",
                        fontWeight: 500,
                        width: "200px", // Match header width
                        borderTopLeftRadius: "10px",
                        borderBottomLeftRadius: "10px",
                        textAlign: "left",
                        fontFamily: 'Josefin Sans',
                      }}
                    >
                      {userData.username}
                    </TableCell>
                    {userData.attendance.map((status, idx) => {
                      const isLast = idx === 30;
                      return (
                        <TableCell
                          key={idx}
                          sx={{
                            fontSize: "0.9rem",
                            color: status === "P" ? "green" : "black",
                            backgroundColor:
                              status === "A"
                                ? "#ffeaea"
                                : status === "W/O"
                                  ? "#d3d3d3"
                                  : status === "In"
                                    ? "#FEA3AA"
                                    : "inherit",
                            textAlign: "center",
                            cursor: status === "P" ? "pointer" : "default",
                            borderRight: isLast ? "1px solid #faf0e0" : "none",
                            transition: "background-color 0.3s, color 0.3s",
                            width: "60px", // Match header width
                            fontFamily: 'Josefin Sans',
                          }}
                          onClick={() => {
                            if (status === "P") {
                              const selectedDate = new Date(
                                selectedYear,
                                selectedMonth - 1,
                                idx + 1
                              ).toLocaleDateString("en-CA");
                              fetchLogDetails(userData.user_id, selectedDate);
                            }
                          }}
                        >
                          {status || "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Modal for displaying log details */}
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 400 }, // Responsive modal width
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" component="h2">
              Login Details
            </Typography>
            {logDetails ? (
              <>
                <Typography sx={{ mt: 2 }}>Time: {logDetails.login_time}</Typography>
                <Typography sx={{ mt: 1 }}>Location: {logDetails.location}</Typography>
              </>
            ) : (
              <Typography sx={{ mt: 2 }}>Loading...</Typography>
            )}
            <Button onClick={() => setModalOpen(false)} sx={{ mt: 3 }} variant="contained">
              Close
            </Button>
          </Box>
        </Modal>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1">Display</Typography>
          <div
            style={{
              border: "3px solid rgb(185, 185, 184)",
              margin: "0 10px",
              padding: "3px 8px",
              borderRadius: "4px",
            }}
          >
            <Typography variant="body1">{itemsPerPage}</Typography>
          </div>
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            sx={{
              color: "#aaa",
              borderRadius: "4px",
              margin: "0 10px",
              height: "30px",
              fontSize: "15px",
              border: "3px solid #a7da6e",
              minWidth: "35px",
              "&:hover": {
                backgroundColor: "#fff",
                color: "#000",
                border: "3px solid #a7da6e",
              },
              "&:disabled": {
                border: "3px solid #a7da6e",
                color: "#aaa",
                cursor: "not-allowed",
              },
            }}
          >
            {"<"}
          </Button>
          <span style={{ margin: "0 10px" }}>
            {currentPage} of {totalPages}
          </span>
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            sx={{
              border: "3px solid #a7da6e",
              color: "black",
              borderRadius: "4px",
              margin: "0 10px",
              height: "30px",
              fontSize: "15px",
              minWidth: "35px",
              "&:hover": {
                backgroundColor: "#fff",
                color: "#000",
                border: "3px solid #a7da6e",
              },
            }}
          >
            {">"}
          </Button>
        </div>
      </Box>
    </Box >
  );
};
